import {
  paginationWithSelect,
  request,
} from '@fastlessjs/backoffice-layer/dist/hooks/stores/helpers';
import { useInfiniteQuery } from '@tanstack/react-query';

import type { DashboardListOptions } from './useDashboardOrders';
import type { DashboardPeriod } from './useDashboardStats';

export type DashboardReturn = {
  suiviId?: string | number;
  orderId?: string;
  enterpriseId?: number;
  clientUserName?: string;
  clientUserEmail?: string;
  createdByRole?: string;
  eurekaStatus?: string;
  _createdAt?: string;
};

type PageParam = { next?: string; pageSize?: number } | undefined;

const PAGE_SIZE = 50;

async function fetchReturnsPage(
  pageParam: PageParam,
  targetOrgId: string,
  period: DashboardPeriod,
) {
  const qs = new URLSearchParams({ period, pageSize: String(PAGE_SIZE) });
  if (pageParam?.next) qs.set('next', pageParam.next);

  return request(`/stats/returns?${qs.toString()}`, {
    method: 'get',
    options: { headers: { 'x-organization': targetOrgId } },
    disableNotif: true,
  });
}

/**
 * Infinite-query hook for the dashboard Returns (Suivi) listing tab.
 *
 * Same contract as {@link useDashboardOrders}: first page → Recent activity,
 * all pages → Listing component.
 */
export const useDashboardReturns = (options?: DashboardListOptions) => {
  const targetOrgId = String(options?.tenantId ?? '');
  const period: DashboardPeriod = (options?.period as DashboardPeriod) ?? 'week';

  return useInfiniteQuery({
    queryKey: ['dashboard-returns', targetOrgId, period],
    queryFn: ({ pageParam }) =>
      fetchReturnsPage(pageParam as PageParam, targetOrgId, period),
    enabled: Boolean(targetOrgId),
    initialPageParam: undefined,
    retry: 1,
    ...paginationWithSelect<DashboardReturn[]>(),
  });
};
